/**
 * Utility for handling API errors from horodaty-api
 *
 * The API returns errors in multiple formats:
 *
 * 1. errorMessage() helper:
 *    { message: string, code: string, data: object }
 *
 * 2. FormattedJsonResponse::error():
 *    { message: string, success: false, status_code: number }
 *
 * 3. Laravel ValidationException:
 *    { message: "... (and X more errors)", errors: { field: [messages] } }
 *
 * L'API localise déjà ses messages (middleware SetLocale + Accept-Language).
 * Les tables ci-dessous ne mappent donc plus vers du français figé mais vers des
 * clés i18n : elles servent de garde-fou quand l'API renvoie une clé Laravel
 * brute (« validation.required ») ou un message anglais non traduit — cas où
 * l'ancienne version imposait du français même en locale en/es.
 */
import i18n from '@/lib/i18n';

/** Champs dont le libellé possède une traduction dédiée (common:fields.*). */
const TRANSLATED_FIELDS = new Set<string>([
    'email',
    'password',
    'password_confirmation',
    'confirm_password',
    'old_password',
    'new_password',
    'name',
    'first_name',
    'last_name',
    'phone',
    'address',
    'city',
    'postal_code',
    'country',
    'company',
    'siret',
    'message',
    'subject',
    'title',
    'description',
    'amount',
    'quantity',
    'date',
    'file',
    'image',
]);

/** Clé de validation Laravel brute -> clé i18n dans common:validation.*. */
const RULE_KEYS: Record<string, string> = {
    'validation.required': 'required',
    'validation.email': 'email',
    'validation.min.string': 'minString',
    'validation.max.string': 'maxString',
    'validation.min.numeric': 'minNumeric',
    'validation.max.numeric': 'maxNumeric',
    'validation.confirmed': 'confirmed',
    'validation.unique': 'unique',
    'validation.exists': 'exists',
    'validation.numeric': 'numeric',
    'validation.integer': 'integer',
    'validation.string': 'string',
    'validation.array': 'array',
    'validation.boolean': 'boolean',
    'validation.date': 'date',
    'validation.date_format': 'dateFormat',
    'validation.before': 'before',
    'validation.after': 'after',
    'validation.image': 'image',
    'validation.mimes': 'mimes',
    'validation.size.file': 'sizeFile',
    'validation.regex': 'regex',
    'validation.alpha': 'alpha',
    'validation.alpha_num': 'alphaNum',
    'validation.url': 'url',
    'validation.in': 'in',
    'validation.not_in': 'notIn',
    'validation.different': 'different',
    'validation.same': 'same',
    'validation.accepted': 'accepted',
    'validation.required_if': 'required',
    'validation.required_with': 'required',
    'validation.required_without': 'required',
};

/**
 * Message d'API non localisé (clé Laravel ou anglais) -> clé i18n dans
 * common:apiErrors.*. Filet de sécurité : en régime normal l'API répond déjà
 * dans la langue demandée et le message passe tel quel.
 */
const API_ERROR_KEYS: Record<string, string> = {
    'These credentials do not match our records.': 'credentialsMismatch',
    'The provided credentials are incorrect.': 'credentialsIncorrect',
    'auth.failed': 'credentialsMismatch',
    'auth.password': 'passwordIncorrect',
    'auth.throttle': 'loginThrottled',
    'The email has already been taken.': 'emailTaken',
    'The email field is required.': 'emailRequired',
    'The email must be a valid email address.': 'emailInvalid',
    'The password must be at least 8 characters.': 'passwordMinLength',
    'The password confirmation does not match.': 'passwordConfirmationMismatch',
    'The password field is required.': 'passwordRequired',
    'The password is incorrect.': 'passwordIncorrect',
    'Too many login attempts. Please try again later.': 'loginThrottled',
    'Your email address is not verified.': 'emailNotVerified',
    'Unauthenticated.': 'unauthenticated',
    'This action is unauthorized.': 'unauthorized',
    'Server Error': 'serverError',
    'Not Found': 'notFound',
    'Service Unavailable': 'serviceUnavailable',
    'Too Many Requests': 'tooManyRequests',
};

/** Libellé traduit d'un champ, ou le nom du champ humanisé s'il n'en a pas. */
export function translateFieldName(field: string): string {
    return TRANSLATED_FIELDS.has(field)
        ? i18n.t(`common:fields.${field}`)
        : field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
}

/** Message générique affiché quand aucune information exploitable n'est disponible. */
export function genericErrorMessage(): string {
    return i18n.t('common:apiErrors.generic');
}

/**
 * Traduit un message d'erreur d'API dans la locale active.
 *
 * Un message déjà localisé par l'API ne correspond à aucune entrée et est
 * retourné tel quel — c'est le chemin normal.
 */
export function translateValidationMessage(message: string): string {
    const apiErrorKey = API_ERROR_KEYS[message];
    if (apiErrorKey) {
        return i18n.t(`common:apiErrors.${apiErrorKey}`);
    }

    const ruleKey = RULE_KEYS[message];
    if (ruleKey) {
        return i18n.t(`common:validation.${ruleKey}`);
    }

    // Clé Laravel brute : tenter la racine (« validation.min.string » -> « validation.min »)
    if (message.startsWith('validation.')) {
        const baseRuleKey = RULE_KEYS[message.split('.').slice(0, 2).join('.')];
        if (baseRuleKey) {
            return i18n.t(`common:validation.${baseRuleKey}`);
        }
        return i18n.t('common:validation.unknownField');
    }

    return message;
}

export interface ValidationErrorResult {
    /** Single error message for display */
    message: string;
    /** Array of all error messages */
    messages: string[];
    /** Errors organized by field */
    fieldErrors: Record<string, string[]>;
}

/**
 * Parse and translate Laravel validation errors from an API response
 */
export function parseValidationErrors(error: unknown): ValidationErrorResult {
    const result: ValidationErrorResult = {
        message: genericErrorMessage(),
        messages: [],
        fieldErrors: {},
    };

    if (!error || typeof error !== 'object') {
        return result;
    }

    const errorObj = error as Record<string, unknown>;
    const response = errorObj.response as Record<string, unknown> | undefined;
    const data = response?.data as Record<string, unknown> | undefined;

    if (!data) {
        // If no response data, check if error has a message directly
        if (typeof errorObj.message === 'string') {
            result.message = translateValidationMessage(errorObj.message);
            result.messages = [result.message];
        }
        return result;
    }

    // Handle Laravel validation errors object { errors: { field: [messages] } }
    // This is the standard format from ValidationException
    const errors = data.errors as Record<string, string[]> | undefined;
    if (errors && typeof errors === 'object') {
        const allMessages: string[] = [];

        Object.entries(errors).forEach(([field, fieldErrors]) => {
            const translatedErrors: string[] = [];

            if (Array.isArray(fieldErrors)) {
                fieldErrors.forEach((err) => {
                    const translated = translateValidationMessage(err);
                    translatedErrors.push(translated);
                    allMessages.push(translated);
                });
            }

            result.fieldErrors[field] = translatedErrors;
        });

        result.messages = allMessages;
        // Build a user-friendly message from all errors
        if (allMessages.length === 1) {
            result.message = allMessages[0];
        } else if (allMessages.length > 1) {
            result.message = allMessages.join('\n');
        }
        return result;
    }

    // Handle errorMessage() helper format: { message: string, code: string, data: object }
    // The API uses this format with translated messages
    if (typeof data.message === 'string') {
        let message = data.message;
        
        // Remove the "(and X more error)" suffix from Laravel messages
        // This pattern comes from Laravel's default validation summary
        message = message.replace(/\s*\(and \d+ more errors?\)\.?$/i, '');
        message = message.replace(/\s*\(et \d+ autres? erreurs?\)\.?$/i, '');
        
        const translated = translateValidationMessage(message.trim());
        
        // If there's additional data with field errors, extract them
        if (data.data && typeof data.data === 'object') {
            const dataObj = data.data as Record<string, unknown>;
            const fieldMessages: string[] = [];
            
            Object.entries(dataObj).forEach(([field, fieldErrors]) => {
                if (Array.isArray(fieldErrors)) {
                    const translatedFieldErrors: string[] = [];
                    fieldErrors.forEach((err) => {
                        if (typeof err === 'string') {
                            const translatedErr = translateValidationMessage(err);
                            translatedFieldErrors.push(translatedErr);
                            fieldMessages.push(translatedErr);
                        }
                    });
                    result.fieldErrors[field] = translatedFieldErrors;
                }
            });
            
            if (fieldMessages.length > 0) {
                result.messages = fieldMessages;
                result.message = fieldMessages.length === 1 
                    ? fieldMessages[0] 
                    : fieldMessages.join('\n');
                return result;
            }
        }
        
        result.message = translated;
        result.messages = [translated];
        return result;
    }

    // Handle error field { error: "..." }
    if (typeof data.error === 'string') {
        const translated = translateValidationMessage(data.error);
        result.message = translated;
        result.messages = [translated];
        return result;
    }

    return result;
}

/**
 * Get a user-friendly error message from any error
 */
export function getErrorMessage(error: unknown, defaultMessage?: string): string {
    const result = parseValidationErrors(error);
    return result.message || defaultMessage || genericErrorMessage();
}

/**
 * Get field-specific errors from a validation error
 */
export function getFieldErrors(error: unknown): Record<string, string[]> {
    const result = parseValidationErrors(error);
    return result.fieldErrors;
}
